import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.addEventListener(SlideshowSelectEvent.eventName, this.#markVideoBesideCounter, { signal });
    for (const counter of this.querySelectorAll('.slideshow-controls__counter')) {
      this.#counterObserver.observe(counter);
    }
    this.#counterObserver.observe(this);
    customElements.whenDefined('slideshow-component').then(this.#markVideoBesideCounter);
  }

  #controller = new AbortController();

  /**
   * Follows the counter's width, which changes with its numbers and breakpoint, and the
   * gallery's own, which decides how many slides a view holds.
   */
  #counterObserver = new ResizeObserver(() => this.#markVideoBesideCounter());

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#counterObserver.disconnect();
  }

  /**
   * On the frame the image counter sits on, a video's play/pause pill goes beside the counter
   * rather than under it (snippets/product-media-gallery-content.liquid). Marks that frame
   * and passes on the counter's width. Only a counter on the left.
   *
   * With one slide per view (mobile) every slide is that frame, so all are marked and a
   * video's pill rides in beside the counter instead of moving there once the swipe has
   * settled. With more (the desktop two-column gallery) only the current slide is.
   */
  #markVideoBesideCounter = () => {
    const { slideshow } = this;
    const counter = [
      ...this.querySelectorAll('slideshow-controls[pagination-position="left"] .slideshow-controls__counter'),
    ].find((element) => element instanceof HTMLElement && element.offsetWidth > 0);
    const slides = counter instanceof HTMLElement ? (slideshow?.slides ?? []) : [];
    const currentSlide = slides[slideshow?.current ?? 0];
    const slideWidth = currentSlide?.getBoundingClientRect().width ?? 0;
    const perView = slideWidth > 0 ? Math.round(this.getBoundingClientRect().width / slideWidth) : 1;
    const marked = new Set(perView > 1 ? [currentSlide] : slides);

    if (counter instanceof HTMLElement) {
      this.style.setProperty('--gallery-counter-width', `${counter.getBoundingClientRect().width}px`);
    }

    for (const slide of this.querySelectorAll('[data-video-beside-counter]')) {
      if (!marked.has(slide)) slide.removeAttribute('data-video-beside-counter');
    }
    for (const slide of marked) {
      slide?.setAttribute('data-video-beside-counter', '');
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (_event) => {
    // No-op: in this theme, non-color variant options (size etc.) never have
    // their own media, so swapping the gallery only produces a flicker. Color
    // is a different product entirely (handled via the page navigation flow),
    // not a variant. Re-enable the swap below if a product ever needs
    // per-variant media.
    //
    // const source = _event.detail.data.html;
    // if (!source) return;
    // const newMediaGallery = source.querySelector('media-gallery');
    // if (!newMediaGallery) return;
    // this.replaceWith(newMediaGallery);
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
